import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import useDebouncedValue from "@/lib/useDebouncedValue";
import { arePlanInputsValid, errorMessage } from "@/lib/errorMessage";
import { fmtCurrencyFull } from "@/lib/format";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from "recharts";
import { AlertTriangle, CheckCircle2, Loader2, History } from "lucide-react";

const Mini = ({ trajectory, survived }) => (
  <div className="h-20" style={{ minHeight: 80 }}>
    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
      <LineChart data={trajectory}>
        <XAxis dataKey="age" hide />
        <YAxis hide />
        <Tooltip
          contentStyle={{ fontSize: 11, background: "#fff", border: "1px solid #E5E2DC", borderRadius: 8 }}
          formatter={(v) => [fmtCurrencyFull(v), "balance"]}
          labelFormatter={(l) => `age ${l}`}
        />
        <Line
          type="monotone"
          dataKey="balance"
          stroke={survived ? "#2B5339" : "#C26747"}
          strokeWidth={1.8}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export default function StressTestPanel({ inputs }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const run = async () => {
    if (!arePlanInputsValid(inputs)) return;
    setLoading(true);
    setErr(null);
    try {
      const { data: res } = await api.post("/projections/stress", inputs);
      setData(res);
    } catch (e) {
      setErr(errorMessage(e, "stress test failed"));
    } finally {
      setLoading(false);
    }
  };

  // Debounce age-sensitive inputs so typing doesn't fire on every keystroke.
  const dAge = useDebouncedValue(inputs.current_age, 600);
  const dRet = useDebouncedValue(inputs.retirement_age, 600);
  const dLife = useDebouncedValue(inputs.life_expectancy, 600);
  const dSavings = useDebouncedValue(inputs.current_savings, 600);
  const dContrib = useDebouncedValue(inputs.monthly_contribution, 600);
  const dWithdraw = useDebouncedValue(inputs.annual_withdrawal, 600);
  const dInflation = useDebouncedValue(inputs.inflation_rate, 600);

  const pending =
    dAge !== inputs.current_age ||
    dRet !== inputs.retirement_age ||
    dLife !== inputs.life_expectancy ||
    dSavings !== inputs.current_savings ||
    dContrib !== inputs.monthly_contribution ||
    dWithdraw !== inputs.annual_withdrawal ||
    dInflation !== inputs.inflation_rate;

  useEffect(() => {
    run();
    // eslint-disable-next-line
  }, [
    dAge,
    dRet,
    dLife,
    dSavings,
    dContrib,
    dWithdraw,
    inputs.allocation.stocks,
    inputs.allocation.bonds,
    inputs.allocation.cash,
    dInflation,
  ]);

  return (
    <div data-testid="stress-panel" className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold inline-flex items-center gap-2">
            <History size={12} /> historical stress test
          </div>
          <div className="font-display text-lg text-[#1C2B23] mt-1">
            What if you retired into a crisis?
          </div>
        </div>
        <button
          data-testid="stress-rerun"
          type="button"
          onClick={run}
          disabled={loading}
          className="text-xs text-[#5C6B62] hover:text-[#2B5339] inline-flex items-center gap-1"
        >
          {(loading || pending) ? <Loader2 size={12} className="animate-spin" /> : null}
          {pending ? "applying changes…" : loading ? "running…" : "re-run"}
        </button>
      </div>

      {err && <div className="text-sm text-[#C26747]">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data?.scenarios ?? Array(4).fill(null)).map((s, i) => (
          <div
            key={s?.key ?? i}
            data-testid={s ? `stress-card-${s.key}` : `stress-card-loading-${i}`}
            className="border border-[#E5E2DC] rounded-xl p-4 bg-[#F9F8F6]/40"
          >
            {s ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm text-[#1C2B23] font-medium">{s.label}</div>
                    <div className="text-[11px] text-[#5C6B62] mt-1 leading-relaxed">
                      {s.description}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold rounded-full px-2.5 py-1 ${
                      s.survived
                        ? "bg-[#E8F0EB] text-[#2B5339]"
                        : "bg-[#FBE9E0] text-[#C26747]"
                    }`}
                  >
                    {s.survived ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                    {s.survived ? "survived" : `ruin @ ${s.first_ruin_age ?? "?"}`}
                  </span>
                </div>
                <Mini trajectory={s.trajectory} survived={s.survived} />
                <div className="flex justify-between text-[11px] text-[#5C6B62] font-mono-num mt-1">
                  <span>final {fmtCurrencyFull(s.final_balance)}</span>
                  <span>min {fmtCurrencyFull(s.min_balance)}</span>
                </div>
              </>
            ) : (
              <div className="h-32 animate-pulse bg-[#F0EFEA] rounded" />
            )}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-[#5C6B62] mt-4 leading-relaxed">
        Each scenario applies the actual annual real returns of that historical era starting at
        your chosen retirement age, then loops the pattern through age {inputs.life_expectancy}.
      </div>
    </div>
  );
}
